import React, { useState, useEffect } from 'react';
import { Ban, X, AlertTriangle, Loader } from 'lucide-react';
import { cn } from '../../../utils/cn';

interface RevokeReasonModalProps {
  isOpen: boolean;
  visitor: { name: string } | null;
  operatorName?: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  darkMode?: boolean;
}

const PRESET_REASONS = [
  'Problema no reconhecimento facial',
  'Visitante não localizado',
  'Comportamento suspeito',
  'Documento inválido ou adulterado',
  'Visita encerrada pelo morador',
  'Outro motivo',
];

export const RevokeReasonModal: React.FC<RevokeReasonModalProps> = ({
  isOpen,
  visitor,
  operatorName,
  onClose,
  onConfirm,
  darkMode = true,
}) => {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedReason(null);
      setFreeText('');
      setIsProcessing(false);
    }
  }, [isOpen]);

  const currentTimestamp = new Date().toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const buildReason = (): string => {
    const chip = selectedReason?.trim() || '';
    const text = freeText.trim();
    if (chip && text) return `${chip}: ${text}`;
    if (chip) return chip;
    if (text) return text;
    return '';
  };

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm(buildReason());
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isProcessing) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-[100] flex items-center justify-center"
      onClick={() => { if (!isProcessing) onClose(); }}
      onKeyDown={handleKeyDown}
    >
      <div
        className={cn(
          "relative rounded-xl shadow-2xl w-full max-w-md mx-4 border",
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Loading Overlay */}
        {isProcessing && (
          <div className={cn(
            "absolute inset-0 bg-opacity-90 z-50 flex items-center justify-center rounded-xl",
            darkMode ? "bg-gray-800" : "bg-white"
          )}>
            <div className="text-center">
              <div className={cn(
                "w-12 h-12 border-4 border-t-red-500 rounded-full animate-spin mx-auto mb-3",
                darkMode ? "border-gray-600" : "border-gray-300"
              )}></div>
              <p className={cn("text-sm font-medium", darkMode ? "text-gray-300" : "text-gray-600")}>Revogando acesso...</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className={cn("px-6 py-4 border-b flex items-center justify-between", darkMode ? "border-gray-700" : "border-gray-200")}>
          <div className="flex items-center gap-3">
            <Ban className={cn("h-6 w-6", darkMode ? "text-red-400" : "text-red-500")} />
            <h3 className={cn("text-lg font-semibold", darkMode ? "text-white" : "text-gray-900")}>Revogar Acesso</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className={cn(
              "transition-colors disabled:opacity-50",
              darkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"
            )}
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">
          {/* Subtitle */}
          <p className={cn("text-base leading-relaxed", darkMode ? "text-gray-300" : "text-gray-600")}>
            Revogar acesso de <span className={cn("font-semibold", darkMode ? "text-white" : "text-gray-900")}>{visitor?.name}</span>?
          </p>

          {/* Reason chips */}
          <div>
            <p className={cn("text-sm font-medium mb-2", darkMode ? "text-gray-400" : "text-gray-500")}>Motivo (opcional)</p>
            <div className="flex flex-wrap gap-2">
              {PRESET_REASONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  onClick={() => setSelectedReason(prev => prev === reason ? null : reason)}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                    selectedReason === reason
                      ? "bg-red-600 border-red-500 text-white"
                      : darkMode
                        ? "bg-gray-700 border-gray-600 text-gray-300 hover:border-red-500/50 hover:text-gray-100"
                        : "bg-gray-100 border-gray-300 text-gray-700 hover:border-red-400 hover:text-gray-900"
                  )}
                >
                  {reason}
                </button>
              ))}
            </div>
          </div>

          {/* Free text */}
          <textarea
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder="Detalhes adicionais (opcional)..."
            rows={3}
            disabled={isProcessing}
            className={cn(
              "w-full px-3 py-2 rounded-lg border resize-none focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50",
              darkMode
                ? "bg-gray-900 border-gray-700 text-gray-100 placeholder-gray-500"
                : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
            )}
          />

          {/* Audit disclaimer */}
          <div className={cn(
            "border rounded-lg p-4",
            darkMode ? "bg-red-900/20 border-red-600/50" : "bg-red-50 border-red-200"
          )}>
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className={cn("h-5 w-5 flex-shrink-0 mt-0.5", darkMode ? "text-red-300" : "text-red-600")} />
              <h4 className={cn("text-sm font-bold uppercase tracking-wide", darkMode ? "text-red-300" : "text-red-700")}>AVISO DE AUDITORIA</h4>
            </div>
            <div className={cn("text-xs leading-relaxed space-y-1", darkMode ? "text-red-200" : "text-red-800")}>
              <p className="mb-2">Esta ação de revogação será registrada com:</p>
              <ul className="space-y-1 mb-3">
                <li>• Seu usuário: {operatorName && <span className={cn("font-semibold", darkMode ? "text-cyan-300" : "text-cyan-700")}>{operatorName}</span>}</li>
                <li>• Data e hora: {currentTimestamp}</li>
                {buildReason() && <li>• Motivo: {buildReason()}</li>}
              </ul>
              <p className="font-semibold">
                A revogação imediata pode afetar o acesso de pessoas autorizadas. Certifique-se de que está revogando o acesso correto.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={cn("px-6 py-4 rounded-b-xl flex justify-end gap-3", darkMode ? "bg-gray-900" : "bg-gray-50")}>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
              darkMode
                ? "text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-100"
            )}
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={isProcessing}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isProcessing && <Loader className="h-4 w-4 animate-spin" />}
            Revogar
          </button>
        </div>
      </div>
    </div>
  );
};
